import React, { useEffect, useRef, useState } from "react";
import { sanitizeRichText } from "./RichTextRenderer";

const BUTTONS = [
  { label: "H1", cmd: "formatBlock", value: "h1", title: "Titre H1" },
  { label: "H2", cmd: "formatBlock", value: "h2", title: "Titre H2" },
  { label: "H3", cmd: "formatBlock", value: "h3", title: "Titre H3" },
  { label: "H4", cmd: "formatBlock", value: "h4", title: "Titre H4" },
  { label: "H5", cmd: "formatBlock", value: "h5", title: "Titre H5" },
  { label: "H6", cmd: "formatBlock", value: "h6", title: "Titre H6" },
  { label: "P", cmd: "formatBlock", value: "p", title: "Paragraphe" },
  { label: "B", cmd: "bold", title: "Gras" },
  { label: "I", cmd: "italic", title: "Italique" },
  { label: "S", cmd: "strikeThrough", title: "Barré" },
  { label: "U", cmd: "underline", title: "Souligné" },
  { label: "•", cmd: "insertUnorderedList", title: "Liste à puces" },
  { label: "1.", cmd: "insertOrderedList", title: "Liste numérotée" },
  { label: "❝", cmd: "formatBlock", value: "blockquote", title: "Citation" },
];

function normalizeInitial(value = "") {
  if (!value) return "";
  const text = String(value);
  return /<\/?[a-z][\s\S]*>/i.test(text) ? text : text.replace(/\n/g, "<br />");
}

export default function RichTextEditor({ value = "", onChange, label, placeholder = "Écris ton texte...", minHeight = 160 }) {
  const editorRef = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const normalized = normalizeInitial(value);
    if (editor.innerHTML !== normalized && !focused) {
      editor.innerHTML = normalized;
    }
  }, [value, focused]);

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const clean = sanitizeRichText(editor.innerHTML);
    onChange?.(clean);
  };

  const runCommand = (cmd, commandValue = null) => {
    editorRef.current?.focus();

    const value = cmd === "formatBlock" && commandValue
      ? `<${commandValue}>`
      : commandValue;

    document.execCommand(cmd, false, value);
    emitChange();
  };

  const addLink = () => {
    const url = window.prompt("URL du lien :");
    if (!url) return;
    runCommand("createLink", url);
  };

  const addDivider = () => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, "<hr />");
    emitChange();
  };

  const addTable = () => {
    editorRef.current?.focus();
    document.execCommand(
      "insertHTML",
      false,
      "<table><tbody><tr><td>Cellule 1</td><td>Cellule 2</td></tr><tr><td>Cellule 3</td><td>Cellule 4</td></tr></tbody></table>"
    );
    emitChange();
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");

    if (html) {
      const clean = sanitizeRichText(html);
      document.execCommand("insertHTML", false, clean);
    } else if (text) {
      const clean = sanitizeRichText(text);
      document.execCommand("insertHTML", false, clean);
    }

    emitChange();
  };

  return (
    <div className="space-y-2">
      {label && <label className="label mb-1 block">{label}</label>}
      <div className="rounded-xl border border-gray-200 bg-white overflow-visible focus-within:ring-2 focus-within:ring-brand-green/20">
        <div className="sticky top-[76px] z-[60] flex flex-wrap items-center gap-1 border-b border-gray-100 bg-gray-50/95 p-2 backdrop-blur-md shadow-sm">
          {BUTTONS.map((button) => (
            <button
              key={`${button.cmd}-${button.value || button.label}`}
              type="button"
              title={button.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runCommand(button.cmd, button.value)}
              className="px-2.5 py-1 rounded-lg text-sm font-bold hover:bg-emerald-100 transition"
            >
              {button.label}
            </button>
          ))}
          <button type="button" title="Lien" onMouseDown={(e) => e.preventDefault()} onClick={addLink} className="px-2.5 py-1 rounded-lg text-sm font-bold hover:bg-emerald-100 transition">🔗</button>
          <button type="button" title="Séparateur" onMouseDown={(e) => e.preventDefault()} onClick={addDivider} className="px-2.5 py-1 rounded-lg text-sm font-bold hover:bg-emerald-100 transition">—</button>
          <button type="button" title="Tableau" onMouseDown={(e) => e.preventDefault()} onClick={addTable} className="px-2.5 py-1 rounded-lg text-sm font-bold hover:bg-emerald-100 transition">▦</button>
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          data-placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); emitChange(); }}
          onInput={emitChange}
          onPaste={handlePaste}
          className="rich-editor-content px-4 py-3 outline-none text-gray-800 leading-relaxed empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
          style={{ minHeight }}
        />
      </div>
    </div>
  );
}
